<script setup>
// Recordatorio de tareas por prioridad (Victor 2026-06-03).
//   - normal:  aviso UNA vez al iniciar sesión.
//   - alta:    modal centrado cada 1 hora mientras siga pendiente.
//   - urgente: modal centrado (pulsa) cada 5 minutos mientras siga pendiente.
// El conteo corre SOLO mientras la persona está conectada (los timers viven en
// esta pestaña; al cerrar sesión se detienen y al volver arrancan de nuevo).
// "Posponer" cierra el modal hasta el próximo ciclo. Un solo modal con la lista.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import TasksAPI from 'dashboard/api/tasks';

const ALTA_MS = 60 * 60 * 1000; // 1 hora
const URGENTE_MS = 5 * 60 * 1000; // 5 minutos
const POLL_MS = 60 * 1000; // refresca la lista de tareas en vivo

const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const myTasks = ref([]);
const level = ref(null); // 'normal' | 'alta' | 'urgente'
const visible = ref(false);
let normalShown = false; // normal: solo una vez por sesión
let timers = [];

const audio = new Audio('/audio/dashboard/bell.mp3');
audio.volume = 0.5;
const ping = () => {
  try {
    audio.currentTime = 0;
    audio.play();
  } catch (_) {
    /* silencioso */
  }
};

const tasksOf = lvl => myTasks.value.filter(t => t.priority === lvl);

const fetchTasks = async () => {
  try {
    const [{ data: a }, { data: b }] = await Promise.all([
      TasksAPI.list({ status: 'pendiente' }),
      TasksAPI.list({ status: 'en_proceso' }),
    ]);
    const all = [...(a?.data?.payload || []), ...(b?.data?.payload || [])];
    const uid = currentUser.value?.id;
    // Backend devuelve TODAS al admin → filtrar a las MÍAS para el recordatorio.
    myTasks.value = all.filter(t => t.assignee_id === uid);
  } catch (_) {
    /* silencioso */
  }
};

// Muestra el recordatorio de un nivel si hay tareas de ese nivel y no hay otro
// modal abierto en ese momento.
const triggerReminder = async lvl => {
  await fetchTasks();
  if (visible.value) return;
  if (tasksOf(lvl).length === 0) return;
  level.value = lvl;
  visible.value = true;
  ping();
};

const postpone = () => {
  // Cierra hasta el próximo ciclo (el timer lo vuelve a mostrar).
  visible.value = false;
};

const markDone = async task => {
  try {
    await TasksAPI.markStatus(task.id, 'hecha');
  } catch (_) {
    /* silencioso */
  }
  myTasks.value = myTasks.value.filter(t => t.id !== task.id);
  // La campana/contador se refresca solo en su próximo poll (cada 60s).
  if (tasksOf(level.value).length === 0) visible.value = false;
};

const openTask = task => {
  if (task.conversation_id && accountId.value) {
    router.push(
      `/app/accounts/${accountId.value}/conversations/${task.conversation_id}`
    );
  } else if (accountId.value) {
    router.push(`/app/accounts/${accountId.value}/conversations`);
  }
  visible.value = false;
};

const firstName = computed(() => (currentUser.value?.name || '').split(' ')[0]);
const levelMeta = computed(() => {
  if (level.value === 'urgente')
    return { title: '⚠ Tareas urgentes', chip: 'Urgente', cadence: 'recordatorio c/5 min', accent: '#e0564b', pulse: true };
  if (level.value === 'alta')
    return { title: '▲ Tareas de prioridad alta', chip: 'Alta', cadence: 'recordatorio c/1 h', accent: '#e0a93c', pulse: false };
  return { title: '📋 Tienes tareas pendientes', chip: 'Normal', cadence: 'al iniciar sesión', accent: '#9aa3af', pulse: false };
});
const listForLevel = computed(() => tasksOf(level.value));

const dueLabel = task => {
  const ts = task.due_date;
  if (!ts) return null;
  const d = new Date(ts * 1000);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dDay = new Date(d);
  dDay.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit', hour12: true });
  const now = Date.now() / 1000;
  if (ts < now) return `vencida · ${time}`;
  if (dDay.getTime() === today.getTime()) return `hoy ${time}`;
  return d.toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

onMounted(async () => {
  await fetchTasks();
  // Normal: una sola vez al entrar.
  if (!normalShown && tasksOf('normal').length > 0) {
    normalShown = true;
    level.value = 'normal';
    visible.value = true;
    ping();
  }
  // Timers de recordatorio recurrente (solo mientras la pestaña esté abierta).
  timers.push(setInterval(() => triggerReminder('urgente'), URGENTE_MS));
  timers.push(setInterval(() => triggerReminder('alta'), ALTA_MS));
  timers.push(setInterval(fetchTasks, POLL_MS));
  emitter.on(BUS_EVENTS.TASK_ASSIGNED_LIVE, fetchTasks);
});

onBeforeUnmount(() => {
  timers.forEach(clearInterval);
  timers = [];
  emitter.off(BUS_EVENTS.TASK_ASSIGNED_LIVE, fetchTasks);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible && listForLevel.length"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/75 backdrop-blur-sm"
    >
      <div
        class="w-[460px] max-w-[95vw] rounded-2xl overflow-hidden shadow-2xl"
        :style="{
          background: 'linear-gradient(180deg, rgba(26,24,20,.98) 0%, rgba(16,15,13,.99) 100%)',
          border: `1px solid ${levelMeta.accent}66`,
          animation: 'alert-rise .5s cubic-bezier(.34,1.56,.64,1)',
        }"
      >
        <!-- Header -->
        <div
          class="p-5 text-center border-b"
          style="border-color: rgba(255,255,255,.06);"
        >
          <div
            class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase"
            :style="{ color: levelMeta.accent, background: `${levelMeta.accent}22` }"
            :class="{ 'reminder-pulse': levelMeta.pulse }"
          >
            {{ levelMeta.chip }} · {{ levelMeta.cadence }}
          </div>
          <div class="text-lg text-zinc-100 font-semibold mt-3">{{ levelMeta.title }}</div>
          <div class="text-[12px] text-zinc-400 mt-1">
            <template v-if="level === 'normal'">
              ¡Hola, <span class="text-zinc-200 font-medium">{{ firstName }}</span>! Tienes
            </template>
            {{ listForLevel.length }} tarea{{ listForLevel.length === 1 ? '' : 's' }} pendiente{{ listForLevel.length === 1 ? '' : 's' }}
          </div>
        </div>

        <!-- Lista -->
        <div class="p-3 max-h-[46vh] overflow-y-auto">
          <div
            v-for="task in listForLevel"
            :key="task.id"
            class="flex items-center gap-3 p-3 rounded-xl mb-2"
            style="background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.05);"
          >
            <div class="flex-1 min-w-0">
              <div class="text-[13px] text-zinc-100 font-medium truncate">{{ task.title }}</div>
              <div class="text-[10px] text-zinc-500 mt-1 truncate">
                Asignada por {{ task.created_by?.name || '—' }}
              </div>
              <div v-if="dueLabel(task)" class="text-[10px] mt-0.5" :style="{ color: levelMeta.accent }">
                ⏰ {{ dueLabel(task) }}
              </div>
            </div>
            <button
              v-if="task.conversation_id"
              class="text-[11px] py-1.5 px-2.5 rounded-lg text-zinc-300 border border-white/10 bg-white/5 hover:bg-white/10 shrink-0"
              @click="openTask(task)"
            >
              Ver
            </button>
            <button
              class="text-[11px] py-1.5 px-2.5 rounded-lg font-semibold shrink-0"
              style="background: linear-gradient(180deg,#e7cf9a,#c9a35a); color:#241c08;"
              @click="markDone(task)"
            >
              ✓ Hecha
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="p-4 border-t text-center bg-black/20"
          style="border-color: rgba(255,255,255,.06);"
        >
          <button
            v-if="level === 'normal'"
            class="text-sm font-bold py-2.5 px-12 rounded-lg"
            style="background: linear-gradient(180deg,#e7cf9a,#c9a35a); color:#241c08;"
            @click="postpone"
          >
            ✓ Entendido
          </button>
          <button
            v-else
            class="text-xs py-2.5 px-8 rounded-lg text-zinc-300 border border-white/10 bg-white/5 hover:bg-white/10"
            @click="postpone"
          >
            Posponer ({{ level === 'urgente' ? '5 min' : '1 h' }})
          </button>
          <div v-if="level === 'normal'" class="text-[10px] text-zinc-500 mt-2.5">
            La tarea queda en el icono de Tareas para que no se te olvide.
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.reminder-pulse {
  animation: reminder-pulse 1.4s ease-in-out infinite;
}
@keyframes reminder-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(224, 86, 75, 0); }
  50% { box-shadow: 0 0 0 5px rgba(224, 86, 75, 0.18); }
}
</style>
